import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, X, Upload, GraduationCap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CLASS_SETS = {
  primary: ["Primary 1", "Primary 2", "Primary 3", "Primary 4", "Primary 5", "Primary 6"],
  secondary: ["JSS 1", "JSS 2", "JSS 3",
    "SSS 1 Science", "SSS 1 Commercial", "SSS 1 Art",
    "SSS 2 Science", "SSS 2 Commercial", "SSS 2 Art",
    "SSS 3 Science", "SSS 3 Commercial", "SSS 3 Art"],
};
const empty = { name: "", level: "primary", logo: "", address: "", phone: "", email: "", active: true, order: 0, lists: {} };

export default function AdminSchools() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [activeClass, setActiveClass] = useState("Primary 1");
  const [uploading, setUploading] = useState(false);
  const [pickQty, setPickQty] = useState(1);
  const [productSearch, setProductSearch] = useState("");

  const { data: schools = [], isLoading } = useQuery({ queryKey: ["admin-schools"], queryFn: () => api.get("/admin/schools").then((r) => r.data) });
  const { data: products = [] } = useQuery({ queryKey: ["admin-products-all"], queryFn: () => api.get("/products", { params: { include_inactive: true, limit: 500 } }).then((r) => r.data) });

  const classes = CLASS_SETS[form.level] || [];
  const items = form.lists?.[activeClass] || [];
  const matches = productSearch.trim()
    ? products.filter((p) => (p.name || "").toLowerCase().includes(productSearch.trim().toLowerCase())).slice(0, 12)
    : [];
  const productName = (slug) => products.find((p) => p.slug === slug)?.name || slug;

  const openNew = () => { setEditing(null); setForm(empty); setActiveClass(CLASS_SETS.primary[0]); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s, lists: s.lists || {} }); setActiveClass((CLASS_SETS[s.level] || CLASS_SETS.primary)[0]); setDialogOpen(true); };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return; setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo: r.data.url })); toast.success("Logo uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const addProduct = (slug) => {
    setForm((f) => {
      const lists = { ...(f.lists || {}) }; const arr = [...(lists[activeClass] || [])];
      const i = arr.findIndex((x) => x.slug === slug);
      const q = Number(pickQty) || 1;
      if (i >= 0) arr[i] = { ...arr[i], qty: q };
      else arr.push({ slug, qty: q });
      lists[activeClass] = arr; return { ...f, lists };
    });
    setProductSearch("");
  };
  const removeItem = (slug) => setForm((f) => { const lists = { ...(f.lists || {}) }; lists[activeClass] = (lists[activeClass] || []).filter((x) => x.slug !== slug); return { ...f, lists }; });
  const setItemQty = (slug, qty) => setForm((f) => { const lists = { ...(f.lists || {}) }; lists[activeClass] = (lists[activeClass] || []).map((x) => (x.slug === slug ? { ...x, qty: Math.max(1, Number(qty) || 1) } : x)); return { ...f, lists }; });

  const save = async () => {
    if (!form.name) { toast.error("School name is required."); return; }
    setSaving(true);
    const lists = Object.fromEntries(
      Object.entries(form.lists || {})
        .map(([k, v]) => [k, (v || []).map((it) => ({ slug: it.slug, qty: Number(it.qty) || 1 }))])
        .filter(([, v]) => v.length > 0)
    );
    const payload = { ...form, order: Number(form.order) || 0, lists };
    try {
      if (editing) await api.put(`/admin/schools/${editing.id}`, payload);
      else await api.post("/admin/schools", payload);
      toast.success(editing ? "School updated" : "School created");
      qc.invalidateQueries({ queryKey: ["admin-schools"] }); setDialogOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`/admin/schools/${deleteId}`); toast.success("School deleted"); qc.invalidateQueries({ queryKey: ["admin-schools"] }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteId(null); }
  };
  const countItems = (s) => Object.values(s.lists || {}).reduce((t, arr) => t + (arr?.length || 0), 0);

  return (
    <div data-testid="admin-schools">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div><h1 className="font-display font-black text-3xl text-slate-900">School Lists</h1><p className="text-slate-500">{schools.length} schools</p></div>
        <button onClick={openNew} data-testid="add-school-btn" className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-brand-blue/90"><Plus className="w-4 h-4" /> Add school</button>
      </div>
      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-semibold">School</th><th className="px-4 py-3 font-semibold">Level</th>
              <th className="px-4 py-3 font-semibold">Items</th><th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th></tr></thead>
            <tbody className="divide-y">
              {schools.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50" data-testid={`school-row-${s.slug}`}>
                  <td className="px-4 py-3"><div className="flex items-center gap-3">
                    {s.logo ? <img src={s.logo} alt="" className="w-9 h-9 rounded object-cover border" /> : <span className="w-9 h-9 flex items-center justify-center bg-slate-100 rounded border"><GraduationCap className="w-4 h-4 text-slate-400" /></span>}
                    <span className="font-medium text-slate-800">{s.name}</span></div></td>
                  <td className="px-4 py-3 capitalize text-slate-600">{s.level}</td>
                  <td className="px-4 py-3 text-slate-600">{countItems(s)}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${s.active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{s.active ? "Active" : "Hidden"}</span></td>
                  <td className="px-4 py-3"><div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(s)} data-testid={`edit-school-${s.slug}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteId(s.id)} data-testid={`delete-school-${s.slug}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button></div></td>
                </tr>
              ))}
              {schools.length === 0 && <tr><td colSpan={5} className="px-4 py-16 text-center text-slate-400">No schools yet</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[840px] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader><DialogTitle>{editing ? "Edit school" : "Add school"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-semibold mb-1">Name *</label>
                <input data-testid="school-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-semibold mb-1">Level</label>
                <select data-testid="school-level" value={form.level} onChange={(e) => { setForm({ ...form, level: e.target.value }); setActiveClass((CLASS_SETS[e.target.value] || [])[0]); }} className="w-full px-3 py-2 border rounded-lg bg-white">
                  <option value="primary">Primary</option><option value="secondary">Secondary</option></select></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-semibold mb-1">Phone</label><input data-testid="school-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
              <div><label className="block text-sm font-semibold mb-1">Email</label><input data-testid="school-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            </div>
            <div><label className="block text-sm font-semibold mb-1">Address</label><input data-testid="school-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                {form.logo ? <img src={form.logo} alt="" className="w-12 h-12 rounded object-cover border" /> : <span className="w-12 h-12 flex items-center justify-center bg-slate-100 rounded border"><GraduationCap className="w-5 h-5 text-slate-400" /></span>}
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg font-medium cursor-pointer">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Logo
                  <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="school-logo-file" />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer ml-auto">
                <input type="checkbox" data-testid="school-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" /> Active
              </label>
            </div>

            <div className="border-t pt-4">
              <p className="font-semibold text-slate-800 mb-2">Book list by class</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {classes.map((c) => (
                  <button key={c} onClick={() => setActiveClass(c)} data-testid={`class-tab-${c}`} className={`px-3 py-1.5 text-sm font-semibold rounded-lg border ${activeClass === c ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-slate-600"}`}>
                    {c}{form.lists?.[c]?.length ? ` (${form.lists[c].length})` : ""}
                  </button>
                ))}
              </div>
              <div className="mb-3">
                <div className="flex gap-2">
                  <input data-testid="school-product-search" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search products to add…" className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue/30" />
                  <input data-testid="school-pick-qty" type="number" min={1} value={pickQty} onChange={(e) => setPickQty(e.target.value)} className="w-20 px-3 py-2 border rounded-lg" title="Quantity" />
                </div>
                {productSearch.trim() && (
                  <div className="mt-1 border rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="school-product-results">
                    {matches.length === 0 ? (
                      <p className="px-3 py-3 text-sm text-slate-400">No matches for “{productSearch}”</p>
                    ) : matches.map((p) => (
                      <button key={p.id} type="button" onClick={() => addProduct(p.slug)} data-testid={`school-result-${p.slug}`} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50">
                        <span className="truncate text-slate-700">{p.name}</span>
                        <span className="shrink-0 inline-flex items-center gap-1 text-xs text-brand-blue font-semibold"><Plus className="w-3.5 h-3.5" /> Add ×{Number(pickQty) || 1}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="border rounded-lg divide-y max-h-56 overflow-y-auto">
                {items.length === 0 ? <p className="px-3 py-6 text-center text-sm text-slate-400">No items for {activeClass} yet</p> : items.map((it) => (
                  <div key={it.slug} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`school-item-${it.slug}`}>
                    <span className="min-w-0 truncate font-medium text-slate-700">{productName(it.slug)}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <input type="number" min={1} value={it.qty} onChange={(e) => setItemQty(it.slug, e.target.value)} className="w-16 px-2 py-1 border rounded" />
                      <button onClick={() => removeItem(it.slug)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 rounded-lg border font-medium">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-school-btn" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-brand-blue text-white font-semibold disabled:opacity-60">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete school?</AlertDialogTitle><AlertDialogDescription>This removes the school and its book lists. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} data-testid="confirm-delete-school" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
